import React from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ReportComponent, ComponentDefinition, COMPONENT_DEFINITIONS } from '@/pages/reports/schemas';
import { ComponentConfigForm } from '@/pages/reports/new/components/ConfigForm';
import type { ThematicSessionTemplate } from '@/pages/reports/utils/thematicSessionTemplates';
import { ThematicTemplatePanel } from '@/pages/reports/new/components/ThematicTemplatePanel';
import { getComponentSummary } from '@/pages/reports/utils/componentSummary';

interface AddedComponentsCardProps {
  components: ReportComponent[];
  editingComponentId: string | null;
  selectedComponent: ComponentDefinition | null;
  componentProps: Record<string, any>;
  validationErrors: Record<string, string>;
  onEditComponent: (_component: ReportComponent) => void;
  onRemoveComponent: (_componentId: string) => void;
  onMoveComponent: (_componentId: string, _direction: 'up' | 'down') => void;
  onPropChange: (_key: string, _value: string | any[]) => void;
  onSaveComponent: () => void;
  onCancelEdit: () => void;
  thematicTemplates: ThematicSessionTemplate[];
  selectedTemplateId: string;
  onTemplateSelect: (_templateId: string) => void;
  onClearAll: () => void;
  onSaveTemplate: (_name: string) => void;
  onUpdateTemplate: (_name: string) => void;
  onDeleteTemplate: () => void;
}

export const AddedComponentsCard: React.FC<AddedComponentsCardProps> = ({
  components,
  editingComponentId,
  selectedComponent,
  componentProps,
  validationErrors,
  onEditComponent,
  onRemoveComponent,
  onMoveComponent,
  onPropChange,
  onSaveComponent,
  onCancelEdit,
  thematicTemplates,
  selectedTemplateId,
  onTemplateSelect,
  onClearAll,
  onSaveTemplate,
  onUpdateTemplate,
  onDeleteTemplate,
}) => {
  if (components.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="border-b">
        <CardTitle className="text-xl">Componentes Adicionados</CardTitle>
        <CardDescription>Gerencie e reordene os componentes do seu report</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 mt-6">
          {components.map((component, index) => {
            const summary = getComponentSummary(component);
            return (
            <div key={component.id}>
              <div 
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer gap-3" 
                onClick={() => onEditComponent(component)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 shrink-0 rounded-full bg-gray-100 flex items-center justify-center font-semibold text-sm text-gray-700">
                    {index + 1}
                  </div>
                  <Badge variant="secondary" className="px-3 py-1 shrink-0">
                    {COMPONENT_DEFINITIONS.find(c => c.type === component.type)?.name}
                  </Badge>
                  {summary && (
                    <span className="text-sm text-gray-600 truncate">
                      {summary}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {component.type !== 'hero' && (
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMoveComponent(component.id, 'up');
                        }}
                        disabled={index === 0}
                        className="p-1 h-8 w-8"
                        title="Mover para cima"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMoveComponent(component.id, 'down');
                        }}
                        disabled={index === components.length - 1}
                        className="p-1 h-8 w-8"
                        title="Mover para baixo"
                      >
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditComponent(component);
                    }}
                  >
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveComponent(component.id);
                    }}
                  >
                    Remover
                  </Button>
                </div>
              </div>
              
              {editingComponentId === component.id && selectedComponent && (
                <div className="mt-3 p-5 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <h4 className="font-semibold text-gray-900 text-lg">
                      Editando {selectedComponent.name}
                    </h4>
                    {selectedComponent.type === 'thematic-session' && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClearAll();
                        }}
                      >
                        Limpar formulario
                      </Button>
                    )}
                  </div>
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mt-1">
                      Edite os campos do componente
                    </p>
                  </div>
                  {selectedComponent.type === 'thematic-session' && (
                    <div className="mb-4">
                      <ThematicTemplatePanel
                        thematicTemplates={thematicTemplates}
                        selectedTemplateId={selectedTemplateId}
                        onTemplateSelect={onTemplateSelect}
                        onSaveTemplate={onSaveTemplate}
                        onUpdateTemplate={onUpdateTemplate}
                        onDeleteTemplate={onDeleteTemplate}
                      />
                    </div>
                  )}
                  <ComponentConfigForm
                    component={selectedComponent}
                    componentProps={componentProps}
                    validationErrors={validationErrors}
                    onPropChange={onPropChange}
                    onSave={onSaveComponent}
                    onCancel={onCancelEdit}
                    saveButtonText="Salvar Alterações"
                  />
                </div>
              )}
            </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
